import { useState } from 'react';
import { Building2, EyeOff, Loader2, Trash2, UploadCloud } from 'lucide-react';
import { Button } from '@sara/ui/components/button';
import { cn } from '@sara/ui/lib/utils';
import { CloudinaryLogo } from '@sara/ui/components/cloudinary-logo';
import { env } from '../../../../config/env';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@sara/ui/components/alert-dialog';

const MAX_BYTES = 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/webp';
const ACCEPTED_TYPES = ACCEPT.split(',');

/**
 * One square 1:1 swatch simulating a light or dark candidate surface, with the
 * logo nested inside as a filled, rounded badge — exactly how the candidate app
 * frames it. Rendered on both surfaces so the org can spot transparency /
 * contrast issues before candidates do.
 */
function LogoPreviewTile({
  theme,
  logoPublicId,
  shown,
}: {
  theme: 'light' | 'dark';
  logoPublicId: string | null;
  shown: boolean;
}) {
  const isDark = theme === 'dark';
  return (
    <div
      className={cn(
        'flex aspect-square min-w-0 flex-1 items-center justify-center rounded-xl border p-5',
        isDark ? 'border-white/10 bg-[oklch(0.21_0.006_265)]' : 'border-black/[0.08] bg-white'
      )}
    >
      {logoPublicId ? (
        <div
          className={cn(
            'flex h-full w-full items-center justify-center overflow-hidden rounded-2xl border shadow-sm transition duration-200',
            isDark ? 'border-white/10' : 'border-black/[0.08]',
            !shown && 'opacity-40 grayscale'
          )}
        >
          {/* Square crop sized generously for the preview tile (retina). */}
          <CloudinaryLogo
            cloudName={env.VITE_CLOUDINARY_CLOUD_NAME}
            publicId={logoPublicId}
            width={384}
            height={384}
            alt={`Organization logo on a ${theme} background`}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <Building2
          strokeWidth={1.5}
          className={cn('size-7', isDark ? 'text-white/30' : 'text-black/25')}
        />
      )}
    </div>
  );
}

interface LogoUploaderProps {
  /** Whether a logo exists — drives drop-zone copy and the remove control. */
  logoUrl: string | null;
  /** Cloudinary public ID used to render the preview tiles. */
  logoPublicId: string | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
  isPending: boolean;
  /** Whether candidates currently see the logo; dims the preview when false. */
  shown?: boolean;
}

export function LogoUploader({
  logoUrl,
  logoPublicId,
  onUpload,
  onRemove,
  isPending,
  shown = true,
}: LogoUploaderProps) {
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  function accept(file: File | undefined) {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError('Logo must be 1MB or smaller.');
      return;
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Logo must be a PNG, JPEG, or WebP image.');
      return;
    }
    setError(null);
    onUpload(file);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    accept(e.target.files?.[0]);
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent<HTMLInputElement>) {
    e.preventDefault();
    setIsDragging(false);
    if (isPending) return;
    accept(e.dataTransfer.files?.[0]);
  }

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 sm:items-stretch">
        {/* Drop zone (left) */}
        <label
          htmlFor="org-logo-file"
          className={cn(
            'group relative flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed px-6 py-7 text-center transition-colors',
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-border bg-secondary/30 hover:border-primary/40 hover:bg-secondary/50',
            isPending && 'pointer-events-none opacity-60'
          )}
        >
          <UploadCloud
            strokeWidth={1.5}
            className={cn(
              'size-6 text-muted-foreground transition-colors',
              isDragging ? 'text-primary' : 'group-hover:text-foreground'
            )}
          />
          <span className="text-[13px] text-foreground">
            {logoUrl ? 'Drop a new logo, or ' : 'Drag & drop, or '}
            <span className="font-medium text-primary">browse</span>
          </span>
          <span className="text-[11.5px] text-muted-foreground">
            PNG, JPEG or WebP · up to 1 MB
          </span>
          {/* Covers the whole dropzone (not just the label text) so drag events land on an
              interactive, focusable element instead of the non-interactive label. */}
          <input
            id="org-logo-file"
            type="file"
            aria-label="Logo file"
            accept={ACCEPT}
            onChange={handleChange}
            onDragOver={(e) => {
              e.preventDefault();
              if (!isPending) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            disabled={isPending}
            className="absolute inset-0 size-full cursor-pointer opacity-0"
          />
        </label>

        {/* Preview (right) — square swatches matching the candidate app, light + dark */}
        <div className="relative flex gap-3">
          <LogoPreviewTile theme="light" logoPublicId={logoPublicId} shown={shown} />
          <LogoPreviewTile theme="dark" logoPublicId={logoPublicId} shown={shown} />
          {isPending && (
            <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-background/55 backdrop-blur-[1px]">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </div>

      {error && <p className="mt-3 text-[13px] text-destructive">{error}</p>}

      {logoUrl && (
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3">
          {shown ? (
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              Replace anytime — uploading a new file overwrites the current logo.
            </p>
          ) : (
            <p className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground">
              <EyeOff className="size-3.5" />
              Hidden from candidates
            </p>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={isPending}
                className="shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 />
                Remove logo
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove organization logo?</AlertDialogTitle>
                <AlertDialogDescription>
                  Candidates will no longer see your logo during conversations.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onRemove}>Remove</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}
